import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  Grid,
  MenuItem,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import CourierLayout from '../CourierLayout';
import { API } from '../../../config';

const ShiftScheduling = () => {
  const [availability, setAvailability] = useState({
    status: 'Available',
    preferredShift: 'Flexible',
  });

  const [timeOff, setTimeOff] = useState({
    date: '',
    reason: '',
  });

  const [assignedShifts, setAssignedShifts] = useState([]);
  const [timeOffRequests, setTimeOffRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  const token =
    localStorage.getItem('courier_token') ||
    localStorage.getItem('token');

  const normalizeList = (data, key) => {
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.[key])) return data[key];
    if (Array.isArray(data?.rows)) return data.rows;
    return [];
  };

  const fetchSchedulingData = async () => {
    try {
      setLoading(true);

      const response = await fetch(`${API}/api/courier/shift-scheduling`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        setAssignedShifts([]);
        setTimeOffRequests([]);
        return;
      }

      const data = await response.json();

      setAssignedShifts(normalizeList(data?.assignedShifts || data?.shifts || [], 'assignedShifts'));
      setTimeOffRequests(normalizeList(data?.timeOffRequests || data?.requests || [], 'timeOffRequests'));

      if (data?.availability) {
        setAvailability({
          status: data.availability.status || 'Available',
          preferredShift: data.availability.preferredShift || data.availability.preferred_shift || 'Flexible',
        });
      }
    } catch (error) {
      console.error('Failed to load shift scheduling:', error);
      setAssignedShifts([]);
      setTimeOffRequests([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSchedulingData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const saveAvailability = async () => {
    try {
      setMessage('');

      const response = await fetch(`${API}/api/courier/availability`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(availability),
      });

      if (!response.ok) {
        throw new Error('Unable to save availability.');
      }

      setMessage('Availability saved.');
    } catch (error) {
      setMessage(error.message || 'Unable to save availability.');
    }
  };

  const submitTimeOff = async () => {
    try {
      setMessage('');

      if (!timeOff.date) {
        setMessage('Please select a date.');
        return;
      }

      if (!timeOff.reason.trim()) {
        setMessage('Please enter a reason.');
        return;
      }

      const response = await fetch(`${API}/api/courier/time-off`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(timeOff),
      });

      if (!response.ok) {
        throw new Error('Unable to submit time-off request.');
      }

      setMessage('Time-off request submitted.');
      setTimeOff({ date: '', reason: '' });
      fetchSchedulingData();
    } catch (error) {
      setMessage(error.message || 'Unable to submit time-off request.');
    }
  };

  const renderEmptyState = (text) => (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 3, md: 4 },
        backgroundColor: '#f8fafc',
        border: '1px solid #e2e8f0',
        borderRadius: 3,
        textAlign: 'center',
      }}
    >
      <Typography
        variant="body2"
        sx={{
          color: '#64748b',
          fontSize: '0.95rem',
        }}
      >
        {text}
      </Typography>
    </Paper>
  );

  return (
    <CourierLayout>
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="h4"
          sx={{
            fontSize: '2rem',
            fontWeight: 700,
            lineHeight: 1.25,
            color: '#0f172a',
            mb: 1,
            textAlign: 'center',
          }}
        >
          Shift Scheduling
        </Typography>

        <Typography
          variant="body2"
          sx={{
            color: '#64748b',
            textAlign: 'center',
            fontSize: '0.95rem',
            lineHeight: 1.5,
          }}
        >
          Manage availability, assigned shifts, and time-off requests.
        </Typography>
      </Box>

      {message && (
        <Alert
          severity={message.includes('saved') || message.includes('submitted') ? 'success' : 'warning'}
          sx={{ mb: 2, borderRadius: 2 }}
        >
          {message}
        </Alert>
      )}

      <Paper
        elevation={1}
        sx={{
          p: { xs: 2, md: 2.5 },
          borderRadius: 3,
          border: '1px solid rgba(15, 23, 42, 0.08)',
          backgroundColor: '#ffffff',
          mb: 2,
        }}
      >
        <Typography
          variant="subtitle1"
          sx={{
            color: '#0f172a',
            fontWeight: 700,
            mb: 0.75,
          }}
        >
          Availability
        </Typography>

        <Typography
          variant="body2"
          sx={{
            color: '#64748b',
            fontSize: '0.95rem',
            mb: 2,
          }}
        >
          Set your current availability for load opportunities.
        </Typography>

        <Divider sx={{ mb: 2 }} />

        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              select
              label="Availability Status"
              fullWidth
              value={availability.status}
              onChange={(event) =>
                setAvailability((prev) => ({
                  ...prev,
                  status: event.target.value,
                }))
              }
            >
              <MenuItem value="Available">Available</MenuItem>
              <MenuItem value="Limited Availability">Limited Availability</MenuItem>
              <MenuItem value="Unavailable">Unavailable</MenuItem>
            </TextField>
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              select
              label="Preferred Shift"
              fullWidth
              value={availability.preferredShift}
              onChange={(event) =>
                setAvailability((prev) => ({
                  ...prev,
                  preferredShift: event.target.value,
                }))
              }
            >
              <MenuItem value="Flexible">Flexible</MenuItem>
              <MenuItem value="Morning">Morning</MenuItem>
              <MenuItem value="Afternoon">Afternoon</MenuItem>
              <MenuItem value="Evening">Evening</MenuItem>
              <MenuItem value="Overnight">Overnight</MenuItem>
            </TextField>
          </Grid>

          <Grid item xs={12}>
            <Box sx={{ display: 'flex', justifyContent: { xs: 'stretch', sm: 'flex-end' } }}>
              <Button
                variant="contained"
                onClick={saveAvailability}
                sx={{
                  minHeight: 42,
                  px: 4,
                  textTransform: 'none',
                  fontWeight: 700,
                  borderRadius: 2,
                  width: { xs: '100%', sm: 'auto' },
                }}
              >
                Save Availability
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      <Paper
        elevation={1}
        sx={{
          p: { xs: 2, md: 2.5 },
          borderRadius: 3,
          border: '1px solid rgba(15, 23, 42, 0.08)',
          backgroundColor: '#ffffff',
          mb: 2,
        }}
      >
        <Typography
          variant="subtitle1"
          sx={{
            color: '#0f172a',
            fontWeight: 700,
            mb: 0.75,
          }}
        >
          Assigned Shifts
        </Typography>

        <Divider sx={{ mb: 2 }} />

        {loading ? (
          renderEmptyState('Loading assigned shifts...')
        ) : assignedShifts.length === 0 ? (
          renderEmptyState('No assigned shifts.')
        ) : (
          <Grid container spacing={2}>
            {assignedShifts.map((shift, index) => (
              <Grid item xs={12} key={shift.id || index}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    borderRadius: 3,
                    backgroundColor: '#f8fafc',
                    border: '1px solid #e2e8f0',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      flexDirection: { xs: 'column', sm: 'row' },
                      gap: 1,
                    }}
                  >
                    <Box>
                      <Typography sx={{ fontWeight: 700, color: '#0f172a' }}>
                        {shift.date || 'Date not available'}
                      </Typography>
                      <Typography sx={{ color: '#64748b', fontSize: '0.95rem' }}>
                        {shift.start_time || shift.startTime || 'Start'} – {shift.end_time || shift.endTime || 'End'}
                      </Typography>
                    </Box>

                    <Chip
                      label={shift.status || 'Assigned'}
                      size="small"
                      sx={{
                        backgroundColor: '#e0f2fe',
                        color: '#0369a1',
                        fontWeight: 700,
                      }}
                    />
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>
        )}
      </Paper>

      <Paper
        elevation={1}
        sx={{
          p: { xs: 2, md: 2.5 },
          borderRadius: 3,
          border: '1px solid rgba(15, 23, 42, 0.08)',
          backgroundColor: '#ffffff',
        }}
      >
        <Typography
          variant="subtitle1"
          sx={{
            color: '#0f172a',
            fontWeight: 700,
            mb: 0.75,
          }}
        >
          Time-Off Request
        </Typography>

        <Typography
          variant="body2"
          sx={{
            color: '#64748b',
            fontSize: '0.95rem',
            mb: 2,
          }}
        >
          Submit time-off requests when you are unavailable.
        </Typography>

        <Divider sx={{ mb: 2 }} />

        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <TextField
              label="Date"
              type="date"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={timeOff.date}
              onChange={(event) =>
                setTimeOff((prev) => ({
                  ...prev,
                  date: event.target.value,
                }))
              }
            />
          </Grid>

          <Grid item xs={12} md={8}>
            <TextField
              label="Reason"
              fullWidth
              value={timeOff.reason}
              onChange={(event) =>
                setTimeOff((prev) => ({
                  ...prev,
                  reason: event.target.value,
                }))
              }
            />
          </Grid>

          <Grid item xs={12}>
            <Box sx={{ display: 'flex', justifyContent: { xs: 'stretch', sm: 'flex-end' } }}>
              <Button
                variant="contained"
                onClick={submitTimeOff}
                sx={{
                  minHeight: 42,
                  px: 4,
                  textTransform: 'none',
                  fontWeight: 700,
                  borderRadius: 2,
                  width: { xs: '100%', sm: 'auto' },
                }}
              >
                Submit Request
              </Button>
            </Box>
          </Grid>
        </Grid>

        {timeOffRequests.length > 0 && (
          <>
            <Divider sx={{ my: 2 }} />

            <Typography
              variant="subtitle1"
              sx={{
                color: '#0f172a',
                fontWeight: 700,
                mb: 1,
              }}
            >
              Previous Requests
            </Typography>

            <Grid container spacing={2}>
              {timeOffRequests.map((request, index) => (
                <Grid item xs={12} key={request.id || index}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      borderRadius: 3,
                      backgroundColor: '#f8fafc',
                      border: '1px solid #e2e8f0',
                    }}
                  >
                    <Typography sx={{ fontWeight: 700, color: '#0f172a' }}>
                      {request.date || 'Date not available'}
                    </Typography>
                    <Typography sx={{ color: '#64748b', fontSize: '0.95rem' }}>
                      {request.reason || 'No reason provided'}
                    </Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </>
        )}
      </Paper>
    </CourierLayout>
  );
};

export default ShiftScheduling;